'use client';

import Goals from './components/Goals';
import Navigation from './components/Navigation';
import Product from './components/Product';
import Roles from './components/Roles';
import Welcome from './components/Welcome';
import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import useOnboardingFormStore from '../dashboard/stores/useOnboardingFormStore';
import { shallow } from 'zustand/shallow';
import { useRouter, useSearchParams } from 'next/navigation';
import { Spinner } from 'flowbite-react';
import { useCognitoUser, UserState } from '../dashboard/hooks/useCognitoUser';
import AmplifyUtils from '../../src/utils/AmplifyUtils';
import welcomePicture from '../../public/assets/images/04_onboaridng_kartiv_step01.jpg';
import optimizePicture from '../../public/assets/images/11_optimize.jpg';
import yourRoles from '../../public/assets/images/12_persona-type.jpg';
import yourGoals from '../../public/assets/images/13_goals.jpg';
import products from '../../public/assets/images/05_onboaridng_kartiv_step02.jpg';
import myProducts from '../../public/assets/images/06_onboaridng_kartiv_step03.jpg';
import useSpaceStore from '../dashboard/stores/useSpaceStore';
import LuckyOrangeScript from '../luckyOrangScript';
import { SessionStorageItemType, UserDataFormType } from '../dashboard/types';
import MyProduct from './components/MyProduct';
import Optimize from './components/Optimize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/sharp-regular-svg-icons';
import useDashboardStore from '../dashboard/stores/useDashboardStore';

interface FormElement {
    id: number;
    name: string;
    component: React.ReactElement;
}

const Onboarding = () => {
    const router = useRouter();
    const params = useSearchParams();
    const [userDataFormType, setUserDataFormType] = useState<string>('');
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [submitedForms, setSubmitedForms] = useState<number[]>([]);
    const [skippedForms, setSkippedForms] = useState<number[]>([]);
    const [isUserFetched, setUserFetched] = useState<boolean>(false);
    const [
        stateOnboarding,
        cognitoUser,
        steps,
        currentFormStep,
        submitedFormStepsIds,
        setCurrentStepToStore,
        getOrCreateUserWithLimits,
        setCognitoUser,
        userOnboarding,
        optimizeSteps,
        submitedOptimizeFormStepsIds,
        currentOptimizeFormStep,
    ] = useOnboardingFormStore(
        (state) => [
            state,
            state.cognitoUser,
            state.steps,
            state.currentFormStep,
            state.submitedFormStepsIds,
            state.setCurrentStepToStore,
            state.getOrCreateUserWithLimits,
            state.setCognitoUser,
            state.user,
            state.optimizeSteps,
            state.submitedOptimizeFormStepsIds,
            state.currentOptimizeFormStep,
        ],
        shallow,
    );
    const [, state, user] = useCognitoUser();
    const [createSpaceIfNotExist] = useSpaceStore((state) => [state.createSpaceIfNotExist], shallow);
    const [prevUrl, setPrevUrl] = useState<string | null>(null);
    const [fetchProducts] = useDashboardStore((state) => [state.fetchProducts], shallow)

    useEffect(() => {
        AmplifyUtils.configureAmplify();
    }, []);

    useEffect(() => {
        if (params?.get('userDataFormType')) {
            setUserDataFormType(params?.get('userDataFormType') as string);
        }
    }, [params]);

    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        window.onpopstate = function () {
            window.history.go(1);
        };
    }, []);

    useEffect(() => {
        if (state === UserState.Authenticated && user) {
            setCognitoUser({ user });
            getOrCreateUserWithLimits({ user });
        } else if (state === UserState.NotAuthenticated) {
            router.push('/');
        }
    }, [getOrCreateUserWithLimits, user, state, setCognitoUser, router]);

    useEffect(() => {
        if (state === UserState.Authenticated && user) {
            createSpaceIfNotExist({ user });
        }
    }, [createSpaceIfNotExist, user, state]);

    useEffect(() => {
        setUserFetched(Boolean(cognitoUser != undefined ? Object.keys(cognitoUser).length : false));
        return () => setUserFetched(false);
    }, [cognitoUser]);

    useEffect(() => {
        if (userDataFormType && userDataFormType === UserDataFormType.OPTIMIZE) {
            if (submitedOptimizeFormStepsIds && currentOptimizeFormStep) {
                setCurrentStep(currentOptimizeFormStep);
                setSubmitedForms(submitedOptimizeFormStepsIds);
            }
        } else {
            if (currentFormStep && submitedFormStepsIds) {
                setCurrentStep(currentFormStep);
                setSubmitedForms(submitedFormStepsIds);
            }
        }
    }, [currentFormStep, submitedFormStepsIds, userDataFormType, submitedOptimizeFormStepsIds, currentOptimizeFormStep]);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        setPrevUrl(sessionStorage.getItem(SessionStorageItemType.PREV_URL_BEFORE_REDIRECTING_TO_OPTIMIZE_FORM))
      },[])

    const formProps = {
        currentStep: currentStep,
        setCurrentStep: setCurrentStep,
        submitedForms: submitedForms,
        setSubmitedForms: setSubmitedForms,
        skippedForms: skippedForms,
        userDataFormType: userDataFormType,
    };

    const formList: FormElement[] = [
        {
            id: 0,
            name: 'Welcome',
            component: <Welcome {...formProps} />,
        },
        {
            id: 1,
            name: 'MyProduct',
            component: <MyProduct {...formProps} />,
        },
    ];

    const optimizeFormList: FormElement[] = [
        {
            id: 0,
            name: 'Optimize',
            component: <Optimize {...formProps} />,
        },
        {
            id: 1,
            name: 'Roles',
            component: <Roles {...formProps} />,
        },
        {
            id: 2,
            name: 'Goals',
            component: <Goals {...formProps} />,
        },
    ];

    return (
        <>
            {isUserFetched && (
                <div className="pl-20 pr-40 pt-6 flex flex-col">
                    <div className="flex items-center justify-between gap-2 place-content-center">
                        {userDataFormType !== UserDataFormType.OPTIMIZE && <div className="flex items-center justify-start gap-2">
                            <Image src="/assets/svg/kartivLogo.svg" alt="logo" width={11} height={16} />
                            <Image src="/assets/svg/kartiv.svg" alt="logo" width={48} height={16} />
                            <div className="flex h-6 px-2.5 py-1 items-center justify-center border rounded border-gray-900">
                                <p className="font-medium text-xs">BETA</p>
                            </div>
                        </div>}
                        {userDataFormType === UserDataFormType.OPTIMIZE && <div
                            className="w-fit py-1 px-2 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-gray-100"
                            onClick={() => window.location.replace(prevUrl ? prevUrl : '/')}
                        >
                            <div className="">
                                <FontAwesomeIcon className="w-4 h-4" icon={faChevronLeft} />
                            </div>
                            <div className="font-medium text-sm">Back</div>
                        </div>}
                    </div>
                    <div className="flex justify-around pt-14">
                        <div>
                            <Navigation {...formProps} />
                        </div>
                        {userDataFormType !== UserDataFormType.OPTIMIZE && (
                            <div className="w-7/12 px-16">{React.cloneElement(formList[currentStep]?.component)}</div>
                        )}
                        {userDataFormType !== UserDataFormType.OPTIMIZE && (
                            <div className=" rounded-lg">
                                {currentStep === 0 && <Image height={330} width={412} src={welcomePicture} alt={'Welcome'} />}
                                {currentStep === 1 && <Image height={330} width={412} src={myProducts} alt={'My Product'} />}
                            </div>
                        )}
                        {userDataFormType === UserDataFormType.OPTIMIZE && (
                            <div className="w-7/12 px-16">{React.cloneElement(optimizeFormList[currentStep]?.component)}</div>
                        )}
                        {userDataFormType === UserDataFormType.OPTIMIZE && (
                            <div className=" rounded-lg">
                                {currentStep === 0 && <Image height={330} width={412} src={optimizePicture} alt={'Optimize'} />}
                                {currentStep === 1 && <Image height={330} width={412} src={yourRoles} alt={'Your Roles'} />}
                                {currentStep === 2 && <Image height={330} width={412} src={yourGoals} alt={'Your Goals'} />}
                            </div>
                        )}
                    </div>
                </div>
            )}
            {!isUserFetched && (
                <div className="mx-auto mt-28 text-center">
                    <Spinner />
                </div>
            )}
            <LuckyOrangeScript />
        </>
    );
};

export default Onboarding;
